import { useGetEnrollmentMultiHomeAddressData, useSubmitEnrollmentMultiHomeAddressData } from '@egw/api'
import {
  Alert,
  AlertType,
  Button,
  GenericError,
  Input,
  ModalDrawer,
  ProgressBar,
  Select,
  useModalState,
} from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useMemo, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useLocation, useNavigate } from 'react-router'
import { passkeyService } from '@egw/auth'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { statesOptions } from '../../../constants/home-address/home-address'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import {
  createAddressLines,
  getSmartyRecommendation,
  isResidentialAddress,
  isSecondaryInfoRequired,
} from '../../../utils/home-address/home-address'

const log = new Logger('Enrollment - Multi - HomeAddress')

interface FormData {
  addressLine1: string
  addressLine2: string
  addressCity: string
  addressRegionCode: string
  addressPostalCode: string
}

export const HomeAddressPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const token = query.get('token') || ''
  const isMobile = passkeyService.getIsMobile()

  const dataLoadPayload = useMemo(() => {
    if (isMobile) {
      return {
        phoneMagicLinkVerificationToken: token,
      }
    }
    return {}
  }, [isMobile])

  useEffect(() => {
    // sendEvent(/* TODO: ADD EVENT FOR LANDING ON PAGE */)
  }, [])

  const { data, isLoading, error } = useGetEnrollmentMultiHomeAddressData(dataLoadPayload)
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiHomeAddressData()
  const { isOpen, closeModal, openModal } = useModalState()
  const [recommendedAddress, setRecommendedAddress] = useState<FormData | null>(null)
  const [continueButtonIsLoading, setContinueButtonIsLoading] = useState<boolean>(false)
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)
  const [hasInitializedForm, setHasInitializedForm] = useState(false)

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''
  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined

  const {
    control,
    watch,
    getValues,
    reset,
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      addressLine1: '',
      addressLine2: '',
      addressCity: '',
      addressRegionCode: '',
      addressPostalCode: '',
    },
    mode: 'onChange',
  })

  // Update form values when data is loaded (only once on initial load)
  useEffect(() => {
    if (!hasInitializedForm && data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData) {
      reset({
        addressLine1: data.enrollmentData.addressLine1 || '',
        addressLine2: data.enrollmentData.addressLine2 || '',
        addressCity: data.enrollmentData.addressCity || '',
        addressRegionCode: data.enrollmentData.addressRegionCode || '',
        addressPostalCode: data.enrollmentData.addressPostalCode || '',
      })
      setHasInitializedForm(true)
    }
  }, [data, reset, hasInitializedForm])

  // Protect route
  useValidateRoute(data?.path)

  const addressLine1 = watch('addressLine1')
  const addressLine2 = watch('addressLine2')
  const addressCity = watch('addressCity')
  const addressRegionCode = watch('addressRegionCode')
  const addressPostalCode = watch('addressPostalCode')

  useEffect(() => {
    if (!hasShownIntent && (addressLine1 || addressLine2 || addressCity || addressRegionCode || addressPostalCode)) {
      setHasShownIntent(true)
      sendEvent('bankmulti_enroll_address_intent', {
        applicationId,
      })
    }
  }, [hasShownIntent, applicationId, addressLine1, addressLine2, addressCity, addressRegionCode, addressPostalCode])

  const onSubmit = async (): Promise<void> => {
    const payload = recommendedAddress ? recommendedAddress : getValues()

    try {
      const response = await mutate({ ...payload, addressCountry: 'US' })

      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_ADDRESS_VERIFIED, {
          applicationId,
        })
        void navigate(response.path)
      } else if (!response?.path) {
        // If no path from BE, redirect to error
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onContinue = async (): Promise<void> => {
    const formData = getValues()
    setContinueButtonIsLoading(true)

    const smartyRecommendedAddress = await getSmartyRecommendation({
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2,
      addressCity: formData.addressCity,
      addressRegionCode: formData.addressRegionCode,
      addressPostalCode: formData.addressPostalCode,
    })

    const isAddressWithIssues = smartyRecommendedAddress
      ? isSecondaryInfoRequired(smartyRecommendedAddress.analysis)
      : false

    if (!smartyRecommendedAddress || (isAddressWithIssues && Boolean(formData.addressLine2))) {
      setContinueButtonIsLoading(false)
      setRecommendedAddress(null)
      return
    }

    if (isSecondaryInfoRequired(smartyRecommendedAddress.analysis)) {
      setContinueButtonIsLoading(false)
      return
    }

    // check if address is not Residential (PO Box, commercial)
    if (!isResidentialAddress(smartyRecommendedAddress.analysis, smartyRecommendedAddress.metadata)) {
      setContinueButtonIsLoading(false)
      return
    }

    setRecommendedAddress(createAddressLines(smartyRecommendedAddress.components))
    setContinueButtonIsLoading(false)
  }

  const handleContinue = async (): Promise<void> => {
    await onContinue()
    openModal()
  }

  return (
    <EnrollmentMultiLayout title={t('multi.home-address.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          <form className="w-full flex-1 flex flex-col justify-start">
            <PageHeader title={t('multi.home-address.title')} className="mb-xs" />
            <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
            <p className="typo-primary-base-regular text-text-meta mb-xl">{t('multi.home-address.description')}</p>

            <div className="flex flex-col gap-sm">
              <Controller
                name="addressLine1"
                control={control}
                rules={{
                  required: t('common.required_field'),
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('multi.home-address.address_line_1')}
                    required
                    error={formErrors.addressLine1?.message}
                    testId="addressLine1_input"
                    isPII
                  />
                )}
              />

              <Controller
                name="addressLine2"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('multi.home-address.address_line_2')}
                    error={formErrors.addressLine2?.message}
                    testId="addressLine2_input"
                    isPII
                  />
                )}
              />

              <Controller
                name="addressCity"
                control={control}
                rules={{
                  required: t('common.required_field'),
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('multi.home-address.city')}
                    required
                    error={formErrors.addressCity?.message}
                    testId="addressCity_input"
                    isPII
                  />
                )}
              />

              <div className="flex gap-sm w-full">
                <div className="w-1/2">
                  <Controller
                    name="addressRegionCode"
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={statesOptions}
                        label={t('multi.home-address.state')}
                        required
                        error={formErrors.addressRegionCode?.message}
                        className="w-full"
                        testId="addressRegionCode_input"
                        isPII
                      />
                    )}
                  />
                </div>
                <div className="w-1/2">
                  <Controller
                    name="addressPostalCode"
                    control={control}
                    rules={{
                      required: t('common.required_field'),
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label={t('multi.home-address.zip')}
                        required
                        error={formErrors.addressCity?.message}
                        className="w-full"
                        maxLength={10}
                        testId="addressPostalCode_input"
                        isPII
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 md:hidden" />

            {submitError ? (
              <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
            ) : null}

            <Button
              type="button"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={continueButtonIsLoading}
              testId="primaryAction_button"
              onClick={() => {
                void handleContinue()
              }}
            >
              {t('common.continue')}
            </Button>

            {/* AUTO GENERATED COMPONENTS */}
          </form>

          <ModalDrawer title={t('multi.home-address.confirm_address')} isOpen={isOpen} onClose={closeModal}>
            {!recommendedAddress ? (
              <p className="typo-primary-base-regular text-text-header mb-md">
                {t('multi.home-address.couldn_find_address')}
              </p>
            ) : null}

            <p className="typo-primary-base-semibold text-text-header fs-exclude">
              {t('multi.home-address.you_entered')}
            </p>
            <p className="typo-primary-base-regular text-text-body fs-exclude">{getValues().addressLine1}</p>
            <p className="typo-primary-base-regular text-text-body fs-exclude">{getValues().addressLine2}</p>
            <p className="typo-primary-base-regular text-text-body fs-exclude">
              {getValues().addressCity}, {getValues().addressRegionCode} {getValues().addressPostalCode}
            </p>

            {recommendedAddress ? (
              <>
                <p className="typo-primary-base-semibold text-text-header mt-md">{t('multi.home-address.we_found')}</p>
                <p className="typo-primary-base-regular text-text-body fs-exclude">{recommendedAddress.addressLine1}</p>
                <p className="typo-primary-base-regular text-text-body fs-exclude">{recommendedAddress.addressLine2}</p>
                <p className="typo-primary-base-regular text-text-body fs-exclude">
                  {recommendedAddress.addressCity}, {recommendedAddress.addressRegionCode}{' '}
                  {recommendedAddress.addressPostalCode}
                </p>
              </>
            ) : null}

            {recommendedAddress ? (
              <Button
                className="w-full mt-lg mb-sm"
                onClick={() => {
                  void onSubmit()
                }}
                testId="btnUseFound_button"
                isLoading={isSubmitting}
              >
                {t('multi.home-address.use_address')}
              </Button>
            ) : null}

            {!recommendedAddress ? (
              <Button
                className="w-full mt-lg mb-sm"
                onClick={() => {
                  void onSubmit()
                }}
                testId="btnUseFound_button"
                isLoading={isSubmitting}
              >
                {t('common.continue')}
              </Button>
            ) : null}

            <Button testId="btnMakeChanges_button" variant="secondary" onClick={closeModal}>
              {t('multi.home-address.make_changes')}
            </Button>
          </ModalDrawer>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
